import { useState, useEffect } from "react";
import { Layout } from "@/components/layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link } from "react-router-dom";
import { MapPin, Search, ArrowRight } from "lucide-react";
import { SEOHead } from "@/components/seo";
import { getCommunitiesWithContent, type CommunityInfo } from "@/data/communityData";

const COUNTY_ORDER = ["Camden", "Burlington", "Gloucester"];

const COUNTY_DESCRIPTIONS: Record<string, string> = {
  Camden: "Camden County is the heart of South Jersey's most walkable and transit-connected communities. From the nationally ranked schools and historic charm of Haddonfield to the vibrant restaurant scene in Collingswood, and from Cherry Hill's suburban convenience to the affordable boroughs along the PATCO corridor, Camden County offers something for every lifestyle and budget. The PATCO Speedline connects many of these communities directly to Center City Philadelphia in under 30 minutes, making Camden County one of the most commuter-friendly areas in the region.",
  Burlington: "Burlington County is New Jersey's largest county by area, stretching from the Delaware River communities of Palmyra and Riverton east through the Pinelands of Shamong and Tabernacle. The county is home to some of South Jersey's most prestigious addresses — Moorestown, Medford, and Marlton — as well as affordable opportunities in Willingboro, Burlington Township, and Mount Holly. The acclaimed Lenape Regional High School District serves several communities, and the NJ Turnpike and I-295 corridors provide access to Philadelphia, Trenton, and beyond.",
  Gloucester: "Gloucester County offers some of South Jersey's best value for families and first-time buyers. Washington Township anchors the county with strong schools and extensive amenities, while Deptford provides major retail and affordable housing. Woodbury's walkable downtown serves as the historic county seat, and West Deptford combines accessibility with well-maintained neighborhoods. Route 42 and I-295 connect Gloucester County communities to Philadelphia in about 20-25 minutes.",
};

const Communities = () => {
  useEffect(() => {
    document.title = "Communities | Prime Realty Partners";
  }, []);

  const [searchTerm, setSearchTerm] = useState("");
  const allCommunities = getCommunitiesWithContent();

  const filtered = allCommunities.filter(
    (c) =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.county.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group by county
  const grouped = COUNTY_ORDER.reduce<Record<string, CommunityInfo[]>>((acc, county) => {
    const items = filtered.filter((c) => c.county === county);
    if (items.length > 0) acc[county] = items;
    return acc;
  }, {});

  const communitiesSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "South Jersey Communities",
    description: "Browse real estate listings by community across South Jersey",
    numberOfItems: allCommunities.length,
    itemListElement: allCommunities.map((community, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "Place",
        name: community.name,
        url: `https://www.prpsj.com/${community.slug}`,
        address: {
          "@type": "PostalAddress",
          addressLocality: community.name,
          addressRegion: community.state,
          addressCountry: "US",
        },
      },
    })),
  };

  return (
    <Layout>
      <SEOHead
        title="Communities | Prime Realty Partners"
        description="Explore South Jersey communities. Browse homes for sale in 49 towns across Burlington, Camden, and Gloucester counties with Prime Realty Partners."
        canonicalUrl="https://www.prpsj.com/communities"
        keywords="South Jersey communities, NJ neighborhoods, homes by town, Camden County real estate, Burlington County homes, Gloucester County"
        schema={communitiesSchema}
        breadcrumbs={[{ name: "Home", url: "https://www.prpsj.com" }, { name: "Communities", url: "https://www.prpsj.com/communities" }]}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary/10 via-background to-background py-16 md:py-24">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              South Jersey Communities
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              Find your perfect neighborhood across Camden, Burlington, and Gloucester Counties.
              Each community page includes local school information, lifestyle details, and live MLS listings.
            </p>
            <div className="max-w-md mx-auto relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                placeholder="Search by town or county..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Communities by County */}
      <section className="py-16">
        <div className="container space-y-12">
          {Object.entries(grouped).map(([county, communities]) => (
            <div key={county}>
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-3">
                {county} County
              </h2>
              {COUNTY_DESCRIPTIONS[county] && (
                <p className="text-muted-foreground leading-relaxed mb-6">
                  {COUNTY_DESCRIPTIONS[county]}
                </p>
              )}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {communities.map((community) => (
                  <Link key={community.slug} to={`/${community.slug}`} className="group">
                    <Card className="hover:shadow-lg transition-shadow h-full">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <h3 className="font-heading text-lg font-semibold group-hover:text-primary transition-colors flex items-center gap-2">
                              <MapPin className="h-4 w-4 text-primary shrink-0" />
                              {community.name}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {community.tagline}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {community.county} County • {community.zips.join(", ")}
                            </p>
                          </div>
                          <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors mt-1 shrink-0" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          ))}

          {Object.keys(grouped).length === 0 && (
            <div className="text-center py-12">
              <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">No Communities Found</h3>
              <p className="text-muted-foreground">
                Try a different search term
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-muted/30">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-3xl font-bold mb-4">
              Can't Find Your Community?
            </h2>
            <p className="text-muted-foreground mb-8">
              Our agents serve many areas across South Jersey. Contact us to learn more about
              available properties in your desired location.
            </p>
            <Button size="lg" asChild>
              <Link to="/contact">Contact an Agent</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Communities;
